<section class="returns-page">
    <header class="page-header">
        <h1>Retouren</h1>
        <p>Welche Filialen zeigen auffällige Retourenwerte?</p>
    </header>

    <section class="summary-grid" aria-label="Retouren Übersicht">
        @if (isLoading()) {
        @for (_ of skeletonSummaryCards; track $index) {
        <div class="summary-card summary-card-skeleton">
            <span class="skeleton-line skeleton-number"></span>
            <span class="skeleton-line skeleton-label"></span>
        </div>
        }
        } @else {
        <button type="button" class="summary-card"
            [class.summary-card-active-neutral]="isSummaryActive(ReturnStatus.Offen)"
            (click)="setFilter(ReturnStatus.Offen)">
            <strong>{{ summaryOffenCount() }}</strong>
            <span>Offen</span>
        </button>

        <div class="summary-card summary-card-disabled">
            <strong>{{ formatWert(summaryHeuteWert()) }} €</strong>
            <span>Heute</span>
        </div>

        <div class="summary-card summary-card-disabled">
            <strong>{{ summaryHeutePositionen() }}</strong>
            <span>Positionen heute</span>
        </div>
        }
    </section>

    @if (isLoading()) {
    <section class="toolbar toolbar-skeleton">
        <div class="filiale-trigger filiale-trigger-skeleton">
            <span class="skeleton-line skeleton-icon"></span>
            <span class="skeleton-line skeleton-select"></span>
            <span class="skeleton-line skeleton-icon"></span>
        </div>

        <div class="filter-tabs">
            @for (_ of skeletonFilterTabs; track $index) {
            <div class="filter-tab filter-tab-skeleton">
                <span class="skeleton-line skeleton-filter-label"></span>
                <span class="skeleton-line skeleton-filter-count"></span>
            </div>
            }
        </div>
    </section>
    } @else {
    <section class="toolbar">
        <label class="filiale-select">
            <button type="button" class="filiale-trigger" (click)="toggleFilialeDropdown()">
                <mat-icon fontSet="material-icons-outlined">storefront</mat-icon>
                <span>{{ selectedFilialeLabel() }}</span>
                <mat-icon class="select-arrow" fontSet="material-icons-outlined">
                    {{ isFilialeDropdownOpen() ? 'expand_less' : 'expand_more' }}
                </mat-icon>
            </button>

            @if (isFilialeDropdownOpen()) {
            <div class="filiale-dropdown">
                @for (filiale of filialen(); track filiale.value) {
                <button type="button" class="filiale-option"
                    [class.filiale-option-active]="selectedFiliale() === filiale.value"
                    (click)="selectFiliale(filiale.value)">
                    {{ filiale.label }}
                </button>
                }
            </div>
            }
        </label>

        <div class="filter-tabs">
            @for (filter of statusFilters(); track filter.value) {
            <button type="button" class="filter-tab" [class.filter-tab-active]="selectedFilter() === filter.value"
                (click)="setFilter(filter.value)">
                <span>{{ filter.label }}</span>
                <strong>{{ filter.count }}</strong>
            </button>
            }
        </div>
    </section>
    }

    <section class="overview-table">
        @if (isLoading()) {
        <div class="table-header">
            <span class="skeleton-line skeleton-header"></span>
            <span class="skeleton-line skeleton-header"></span>
            <span class="skeleton-line skeleton-header"></span>
            <span class="skeleton-line skeleton-header"></span>
            <span class="skeleton-line skeleton-header"></span>
            <span class="skeleton-line skeleton-header"></span>
            <span class="skeleton-line skeleton-header"></span>
            <span class="skeleton-line skeleton-header"></span>
        </div>
        } @else if (filteredReturns().length) {
        <div class="table-header">
            <button type="button" class="table-sort-button" [class.table-sort-button-active]="isSortedBy('filiale')"
                (click)="setSort('filiale')">
                <span>Filiale</span>

                <mat-icon fontSet="material-icons-outlined">
                    {{ getSortIcon('filiale') }}
                </mat-icon>
            </button>

            <span>Datum</span>

            <button type="button" class="table-sort-button" [class.table-sort-button-active]="isSortedBy('status')"
                (click)="setSort('status')">
                <span>Status</span>

                <mat-icon fontSet="material-icons-outlined">
                    {{ getSortIcon('status') }}
                </mat-icon>
            </button>

            <button type="button" class="table-sort-button table-sort-button-numeric"
                [class.table-sort-button-active]="isSortedBy('wert')" (click)="setSort('wert')">
                <span>Wert €</span>

                <mat-icon fontSet="material-icons-outlined">
                    {{ getSortIcon('wert') }}
                </mat-icon>
            </button>

            <button type="button" class="table-sort-button table-sort-button-numeric"
                [class.table-sort-button-active]="isSortedBy('quote')" (click)="setSort('quote')">
                <span>Quote</span>

                <mat-icon fontSet="material-icons-outlined">
                    {{ getSortIcon('quote') }}
                </mat-icon>
            </button>

            <span>Pos.</span>
            <!-- V2: <span>Hauptgrund</span> -->
            <span>Aktion</span>
        </div>
        }

        <div class="table-body">
            @if (isLoading()) {
            @for (_ of skeletonRows; track $index) {
            <div class="table-row table-row-skeleton">
                <div class="cell">
                    <span class="skeleton-line skeleton-title"></span>
                    <span class="skeleton-line skeleton-subtitle"></span>
                </div>

                <div class="cell">
                    <span class="skeleton-line skeleton-short"></span>
                </div>

                <div class="cell">
                    <span class="skeleton-line skeleton-pill"></span>
                </div>

                <div class="cell">
                    <span class="skeleton-line skeleton-short"></span>
                </div>

                <div class="cell">
                    <span class="skeleton-line skeleton-short"></span>
                </div>

                <div class="cell">
                    <span class="skeleton-line skeleton-short"></span>
                </div>

                <div class="cell">
                    <span class="skeleton-line skeleton-medium"></span>
                </div>

                <div class="cell">
                    <span class="skeleton-line skeleton-action"></span>
                </div>
            </div>
            }
            } @else if (!filteredReturns().length) {
            <div class="empty-state">
                Keine Retouren für den gewählten Filter.
            </div>
            } @else {
            @for (retoure of filteredReturns(); track retoure.filialNummer) {
            <div class="table-row table-row-clickable" tabindex="0" (click)="openReturn(retoure)">
                <div class="cell filiale-cell">
                    <strong>{{ retoure.filialeName }}</strong>
                    <small>{{ retoure.filialNummer }}</small>
                </div>

                <div class="cell datum-cell">
                    <span>{{ retoure.datum }}</span>

                    @if (isUeberfaelligSichtbar(retoure)) {
                    <small class="ueberfaellig-badge">Überfällig</small>
                    }
                </div>

                <div class="cell status-cell">
                    <span class="status-pill" [ngClass]="getStatusClass(retoure.status)">
                        {{ getStatusLabel(retoure.status) }}
                    </span>
                </div>

                <div class="cell numeric-cell">
                    <span>
                        {{ formatWert(retoure.wert) }} €
                    </span>
                </div>

                <div class="cell numeric-cell">
                    <span>
                        {{ formatQuote(retoure.quote) }}
                    </span>
                </div>

                <div class="cell numeric-cell">
                    <span>{{ retoure.anzahlPositionen }}</span>
                </div>

                <!-- V2:
                <div class="cell">
                    <span>{{ retoure.hauptgrund }}</span>
                </div>
                -->

                <div class="cell action-cell">
                    <button type="button" (click)="
                                $event.stopPropagation();
                                openReturn(retoure)
                            ">
                        {{ getActionLabel(retoure) }}
                    </button>
                </div>
            </div>
            }
            }
        </div>
    </section>

    @if (isLoading()) {
    <footer class="overview-footer overview-footer-skeleton">
        <span class="skeleton-line skeleton-footer-item footer-item-inline"></span>
        <span class="skeleton-line skeleton-footer-item footer-item-inline"></span>
        <span class="skeleton-line skeleton-footer-item footer-item-block"></span>
    </footer>
    } @else {
    <footer class="overview-footer">
        <span class="footer-item-inline">Gesamt: <strong>{{ totalCount() }}</strong></span>
        <span class="footer-item-inline">Erledigt: <strong>{{ erledigtCount() }}/{{ totalCount() }}</strong></span>
        <span class="footer-item-block">Retourenwert heute: <strong class="footer-wert-heute">{{
                formatWert(footerWertHeute()) }} €</strong></span>
    </footer>
    }
</section>